import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { feature as topojsonFeature } from "topojson-client";
import {
  Paper,
  Typography,
  IconButton,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const PREFECTURE_SVG_URL =
  "https://raw.githubusercontent.com/geolonia/japanese-prefectures/master/map-full.svg";
const JPN_ATLAS_URL = "https://unpkg.com/jpn-atlas@1/japan/japan.json";
const BORDER_GRAY = "#a8a8a8";
const NO_DATA_FILL = "#eeeeee";
const TOKYO_ISLAND_CITY_CODES = new Set([
  "13361",
  "13362",
  "13363",
  "13364",
  "13381",
  "13382",
  "13401",
  "13402",
  "13421",
]);
const PREF_CODE_BY_NAME = {
  北海道: "1",
  青森県: "2",
  岩手県: "3",
  宮城県: "4",
  秋田県: "5",
  山形県: "6",
  福島県: "7",
  茨城県: "8",
  栃木県: "9",
  群馬県: "10",
  埼玉県: "11",
  千葉県: "12",
  東京都: "13",
  神奈川県: "14",
  新潟県: "15",
  山梨県: "19",
  長野県: "20",
};

const colorScale = d3
  .scaleThreshold()
  .domain([10, 20, 30, 40, 50, 70, 90])
  .range([
    "#fff7ec",
    "#fee8c8",
    "#fdd49e",
    "#fdbb84",
    "#fc8d59",
    "#ef6548",
    "#d7301f",
    "#990000",
  ]);

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: theme.spacing(1),
      gap: theme.spacing(1),
      position: "relative",
      zIndex: 3,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
    },
    titleBlock: {
      minWidth: 0,
    },
    title: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.3,
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      lineHeight: 1.4,
    },
    backButton: {
      padding: 10,
      marginTop: -2,
      zIndex: 4,
      background: "#fff",
    },
    mapPaper: {
      position: "relative",
      minHeight: 640,
      padding: theme.spacing(2),
      overflow: "hidden",
      width: "80%",
      margin: "auto",
    },
    mapLayout: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(2),
      width: "100%",
    },
    mapColumn: {
      flex: "0 0 80%",
      minWidth: 0,
      display: "flex",
      justifyContent: "center",
      paddingLeft: "10%",
    },
    legendColumn: {
      flex: "0 0 20%",
      minWidth: 150,
      paddingTop: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    mapStage: {
      position: "relative",
      width: "100%",
      minHeight: 560,
      background: "#fff",
    },
    mapHost: {
      width: "92%",
      display: "flex",
      justifyContent: "center",
      "& svg": {
        width: "100%",
        height: "auto",
        display: "block",
      },
      "& .geolonia-svg-map .prefecture": {
        cursor: "pointer",
      },
    },
    municipalitySvg: {
      width: "100%",
      height: "auto",
      display: "block",
      margin: "0 auto",
    },
    labelLayer: {
      pointerEvents: "none",
    },
    mapLabel: {
      fontSize: 9,
      fontWeight: 700,
      fill: "#333",
      paintOrder: "stroke",
      stroke: "#fff",
      strokeWidth: 3,
      strokeLinejoin: "round",
      textAnchor: "middle",
      dominantBaseline: "middle",
    },
    tooltip: {
      position: "absolute",
      zIndex: 20,
      minWidth: 200,
      maxWidth: 280,
      background: "rgba(0, 0, 0, 0.86)",
      color: "#fff",
      borderRadius: 8,
      padding: "10px 12px",
      fontSize: 12,
      lineHeight: 1.6,
      pointerEvents: "none",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
    },
    loading: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.secondary,
      fontSize: 13,
      background: "rgba(255, 255, 255, 0.55)",
    },
    error: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.error.main,
      fontSize: 13,
      background: "rgba(255, 255, 255, 0.55)",
      textAlign: "center",
      padding: theme.spacing(2),
    },
    legend: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(0.5),
      marginTop: 0,
      justifyContent: "flex-start",
      paddingLeft: theme.spacing(1),
    },
    legendLabel: {
      fontSize: 12,
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    legendItem: {
      display: "inline-flex",
      alignItems: "center",
      marginRight: 0,
      marginBottom: theme.spacing(0.25),
      fontSize: 11,
      color: theme.palette.text.secondary,
    },
    legendSwatch: {
      width: 14,
      height: 14,
      border: "1px solid #ccc",
      marginRight: theme.spacing(0.5),
    },
  })
);

const legendItems = [
  { label: "0%", value: 0.01 },
  { label: "10%", value: 10.01 },
  { label: "20%", value: 20.01 },
  { label: "30%", value: 30.01 },
  { label: "40%", value: 40.01 },
  { label: "50%", value: 50.01 },
  { label: "70%", value: 70.01 },
  { label: "90%+", value: 90.01 },
];

const normalizePrefCode = (code) => {
  if (code === null || code === undefined || code === "") {
    return null;
  }

  return String(code).padStart(2, "0");
};

const normalizePrefCodeForSvg = (code) => {
  if (code === null || code === undefined || code === "") {
    return null;
  }

  const normalized = String(code).replace(/^0+/, "");
  return normalized === "" ? "0" : normalized;
};

const normalizeCityCode = (code) => {
  if (code === null || code === undefined || code === "") {
    return null;
  }

  return String(code).padStart(5, "0");
};

const normalizeLookupText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).replace(/\s+/g, "");
};

const getRowValue = (row, keys, fallback = null) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }

  return fallback;
};

const normalizeRow = (row) => {
  if (Array.isArray(row)) {
    return {
      name: row[1] || "",
      registered: Number(row[2]) || 0,
      unshot: Number(row[3]) || 0,
      reviewOk: Number(row[4]) || 0,
      achievedPercent: Number(row[5]) || 0,
      participants: Number(row[6]) || 0,
      cityName: null,
      cityAreaId: null,
      cityAreaCode: null,
      prefectureName: row[1] || "",
      prefectureCodePadded: null,
      prefectureAreaId: null,
      prefectureCode: null,
    };
  }

  const registered = Number(
    getRowValue(row, ["registered", "count", "registered_count"], 0)
  ) || 0;
  const reviewOk = Number(getRowValue(row, ["reviewOk", "reviewok_count"], 0)) || 0;
  const unshot =
    Number(getRowValue(row, ["unshot"], Math.max(registered - reviewOk, 0))) ||
    0;

  return {
    name:
      getRowValue(row, ["name", "cityName", "city_name", "prefectureName", "prefecture_name"], "") ||
      "",
    registered,
    unshot,
    reviewOk,
    achievedPercent:
      Number(
        getRowValue(row, ["achievedPercent", "achieved_percent"], 0)
      ) || 0,
    participants:
      Number(getRowValue(row, ["participants", "participants_count"], 0)) || 0,
    cityName: getRowValue(row, ["cityName", "city_name"], null),
    cityAreaId: getRowValue(row, ["cityAreaId", "city_area_id"], null),
    cityAreaCode: normalizeCityCode(
      getRowValue(row, ["cityAreaCode", "city_area_code"], null)
    ),
    prefectureName: getRowValue(
      row,
      ["prefectureName", "prefecture_name"],
      getRowValue(row, ["name"], null)
    ),
    prefectureAreaId: getRowValue(row, ["prefectureAreaId", "prefecture_area_id"], null),
    prefectureCode: normalizePrefCodeForSvg(
      getRowValue(row, ["prefectureCode", "prefecture_code"], null)
    ),
    prefectureCodePadded: normalizePrefCode(
      getRowValue(row, ["prefectureCode", "prefecture_code"], null)
    ),
  };
};

const normalizeRows = (rows = []) => rows.map((row) => normalizeRow(row));

const buildTooltipLines = (row) => {
  if (!row) {
    return ["データなし"];
  }

  return [
    `登録数: ${row.registered.toLocaleString()}`,
    `レビューOK撮影数: ${row.reviewOk.toLocaleString()}`,
    `達成率: ${row.achievedPercent.toFixed(2)}%`,
    `撮影者数: ${row.participants.toLocaleString()}`,
  ];
};

const applyFillToNode = (node, fillColor) => {
  const shapeSelector = "path, polygon, polyline, rect, circle, ellipse";
  node.style.setProperty("fill", fillColor, "important");
  node.querySelectorAll(shapeSelector).forEach((shape) => {
    shape.style.setProperty("fill", fillColor, "important");
  });
};

const applyStrokeToNode = (node, strokeColor, strokeWidth) => {
  const shapeSelector = "path, polygon, polyline, rect, circle, ellipse";
  node.style.setProperty("stroke", strokeColor, "important");
  node.style.setProperty("stroke-width", strokeWidth, "important");
  node.querySelectorAll(shapeSelector).forEach((shape) => {
    shape.style.setProperty("stroke", strokeColor, "important");
    shape.style.setProperty("stroke-width", strokeWidth, "important");
  });
};

const clearLabelLayer = (svg) => {
  d3.select(svg).selectAll("g.by-area-label-layer").remove();
};

const appendPrefectureLabels = (svg, rowByPrefCode, classes) => {
  if (!svg) {
    return;
  }

  clearLabelLayer(svg);
  d3.select(svg).selectAll("text").remove();

  const layer = d3
    .select(svg)
    .append("g")
    .attr("class", "by-area-label-layer")
    .attr("pointer-events", "none");

  Array.from(svg.querySelectorAll("[data-code]")).forEach((node) => {
    const code = String(node.getAttribute("data-code") || "");
    const row = rowByPrefCode[code];
    const label = row ? row.prefectureName : "";
    if (!label) {
      return;
    }

    try {
      const box = node.getBBox();
      if (box.width < 16 || box.height < 10) {
        return;
      }
      const screenPoint = svg.createSVGPoint();
      const screenBox = node.getBoundingClientRect();
      const svgScreenMatrix = svg.getScreenCTM();
      if (!svgScreenMatrix) {
        return;
      }

      screenPoint.x = screenBox.left + screenBox.width / 2;
      screenPoint.y = screenBox.top + screenBox.height / 2;
      const transformed = screenPoint.matrixTransform(svgScreenMatrix.inverse());

      layer
        .append("text")
        .attr("class", classes.mapLabel)
        .attr("x", transformed.x + 4)
        .attr("y", transformed.y)
        .text(label);
    } catch (err) {
      // ignore invisible or zero-size nodes
    }
  });
};

const aggregatePrefectureRows = (rows = []) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const prefectureName = row.prefectureName || row.name || "";
    const prefectureCode = normalizePrefCodeForSvg(
      row.prefectureCode || PREF_CODE_BY_NAME[prefectureName]
    );
    const prefectureCodePadded = normalizePrefCode(prefectureCode);
    const key = prefectureCode || prefectureName;
    const current = grouped.get(key) || {
      ...row,
      name: prefectureName,
      prefectureName,
      prefectureCode,
      prefectureCodePadded,
      registered: 0,
      unshot: 0,
      reviewOk: 0,
      participants: 0,
      cityRows: [],
    };

    current.registered += row.registered || 0;
    current.unshot += row.unshot || 0;
    current.reviewOk += row.reviewOk || 0;
    current.participants += row.participants || 0;

    if (row.cityName) {
      current.cityRows.push(row);
    }

    current.achievedPercent = current.registered
      ? Number(((current.reviewOk / current.registered) * 100).toFixed(2))
      : 0;

    grouped.set(key, current);
  });

  return Array.from(grouped.values()).sort((a, b) => b.registered - a.registered);
};

const getFeatureCode = (feature) => {
  if (!feature) {
    return null;
  }

  const rawCode =
    feature.id ||
    feature.properties?.id ||
    feature.properties?.code ||
    feature.properties?.adminCode ||
    feature.properties?.N03_007 ||
    null;

  return rawCode ? String(rawCode) : null;
};

let jpnAtlasPromise = null;

const loadJapanAtlas = () => {
  if (!jpnAtlasPromise) {
    jpnAtlasPromise = fetch(JPN_ATLAS_URL).then(async (response) => {
      if (!response.ok) {
        throw new Error(`jpn-atlas load failed (${response.status})`);
      }

      return response.json();
    });
  }

  return jpnAtlasPromise;
};

const MunicipalityMap = ({ selectedPrefecture, onHover, onLeave }) => {
  const classes = useStyles();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const prefectureRows = useMemo(() => {
    return selectedPrefecture ? selectedPrefecture.cityRows || [] : [];
  }, [selectedPrefecture]);

  const rowByCityName = useMemo(() => {
    return prefectureRows.reduce((acc, row) => {
      const cityName = normalizeLookupText(row.cityName);
      if (cityName) {
        acc[cityName] = row;
      }
      return acc;
    }, {});
  }, [prefectureRows]);

  const rowByCityCode = useMemo(() => {
    return prefectureRows.reduce((acc, row) => {
      const code = normalizeCityCode(row.cityAreaCode);
      if (code) {
        acc[code] = row;
      }
      return acc;
    }, {});
  }, [prefectureRows]);

  const featureCollection = useMemo(
    () => ({
      type: "FeatureCollection",
      features: features.map((item) => item.feature).filter(Boolean),
    }),
    [features]
  );

  useEffect(() => {
    let cancelled = false;

    const loadFeatures = async () => {
      if (!selectedPrefecture || prefectureRows.length === 0) {
        setFeatures([]);
        setError(
          selectedPrefecture
            ? "この県に市区町村データがありません"
            : null
        );
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const prefCode =
          selectedPrefecture.prefectureCodePadded ||
          normalizePrefCode(selectedPrefecture.prefectureCode);
        const atlas = await loadJapanAtlas();
        const municipalityObject = atlas?.objects?.municipalities;
        if (!municipalityObject) {
          throw new Error("jpn-atlas municipalities object not found");
        }

        const atlasFeatures =
          topojsonFeature(atlas, municipalityObject)?.features || [];
        const prefCodePrefix = String(prefCode || "").padStart(2, "0");
        const selectedFeatures = atlasFeatures
          .map((feature) => {
            const featureCode = normalizeCityCode(getFeatureCode(feature));
            if (!featureCode || featureCode.slice(0, 2) !== prefCodePrefix) {
              return null;
            }

            if (
              prefCodePrefix === "13" &&
              TOKYO_ISLAND_CITY_CODES.has(featureCode)
            ) {
              return null;
            }

            const cityName =
              feature?.properties?.name ||
              feature?.properties?.N03_004 ||
              "";
            const row =
              rowByCityCode[featureCode] ||
              rowByCityName[normalizeLookupText(cityName)] ||
              null;

            return {
              feature,
              row,
              cityName,
              cityAreaId: row?.cityAreaId || null,
            };
          })
          .filter(Boolean);

        if (cancelled) {
          return;
        }

        setFeatures(selectedFeatures);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "市区町村地図の読み込みに失敗しました");
          setFeatures([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFeatures();

    return () => {
      cancelled = true;
    };
  }, [prefectureRows, selectedPrefecture]);

  const projection = useMemo(() => {
    if (!features.length) {
      return null;
    }

    return d3.geoIdentity().fitSize([850, 680], featureCollection);
  }, [featureCollection, features.length]);

  const path = useMemo(() => {
    if (!projection) {
      return null;
    }

    return d3.geoPath(projection);
  }, [projection]);

  const municipalityLabels = useMemo(() => {
    if (!path) {
      return [];
    }

    const labels = new Map();

    features.forEach(({ feature, row, cityName }, index) => {
      if (!row) {
        return;
      }

      const centroid = path.centroid(feature);
      const bounds = path.bounds(feature);
      const label =
        row?.cityName ||
        cityName ||
        feature?.properties?.name ||
        feature?.properties?.N03_004 ||
        "";

      if (
        !label ||
        !centroid ||
        Number.isNaN(centroid[0]) ||
        Number.isNaN(centroid[1])
      ) {
        return;
      }

      const width = Math.abs(bounds[1][0] - bounds[0][0]);
      const height = Math.abs(bounds[1][1] - bounds[0][1]);
      if (width < 24 || height < 12) {
        return;
      }

      const key = row?.cityAreaId || row?.cityAreaCode || label;
      if (labels.has(key)) {
        return;
      }

      labels.set(key, {
        key: [getFeatureCode(feature) || row?.cityAreaCode || label, index].join("-"),
        label,
        x: centroid[0],
        y: centroid[1],
      });
    });

    return Array.from(labels.values());
  }, [features, path]);

  return (
    <>
      {!loading && !error && features.length > 0 && path && (
        <svg
          className={classes.municipalitySvg}
          viewBox="0 0 850 680"
          preserveAspectRatio="xMidYMid meet"
        >
          <g>
            {features.map(({ feature, row }, index) => {
              if (!feature) {
                return null;
              }

              const code = getFeatureCode(feature) || row?.cityAreaCode;
              const d = path(feature);
              if (!d) {
                return null;
              }

              const fillColor = row ? colorScale(row.achievedPercent) : NO_DATA_FILL;

              return (
                <path
                  key={`${code || "feature"}-${index}`}
                  d={d}
                  fill={fillColor}
                  stroke={BORDER_GRAY}
                  strokeWidth="1.15"
                  onMouseEnter={(event) => onHover(event, row, feature)}
                  onMouseLeave={onLeave}
                  style={{ cursor: row ? "default" : "help" }}
                />
              );
            })}
          </g>
          <g className={classes.labelLayer}>
            {municipalityLabels.map((item) => (
              <text
                key={item.key}
                className={classes.mapLabel}
                x={item.x}
                y={item.y}
              >
                {item.label}
              </text>
            ))}
          </g>
        </svg>
      )}

      {loading && <div className={classes.loading}>市区町村地図を読み込み中...</div>}
      {error && <div className={classes.error}>{error}</div>}
      {!loading && !error && !features.length && selectedPrefecture && (
        <div className={classes.error}>市区町村データがありません</div>
      )}
    </>
  );
};

const ByAreaMap = ({ data = [] }) => {
  const classes = useStyles();
  const mapHostRef = useRef(null);
  const mapStageRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [svgReadyKey, setSvgReadyKey] = useState(0);
  const [selectedPrefecture, setSelectedPrefecture] = useState(null);
  const [prefectureHtml, setPrefectureHtml] = useState("");

  const rows = useMemo(() => normalizeRows(data), [data]);
  const prefectureRows = useMemo(() => aggregatePrefectureRows(rows), [rows]);
  const rowByPrefCode = useMemo(() => {
    return prefectureRows.reduce((acc, row) => {
      const code = normalizePrefCodeForSvg(
        row.prefectureCode || PREF_CODE_BY_NAME[row.prefectureName]
      );
      if (code) {
        acc[code] = row;
      }
      return acc;
    }, {});
  }, [prefectureRows]);

  useEffect(() => {
    setSelectedPrefecture(null);
    setTooltip(null);
  }, [rows]);

  useEffect(() => {
    let mounted = true;

    const loadSvg = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(PREFECTURE_SVG_URL);
        if (!response.ok) {
          throw new Error(`SVG load failed (${response.status})`);
        }

        const svgText = await response.text();
        if (!mounted) {
          return;
        }

        setPrefectureHtml(svgText);
        setSvgReadyKey((current) => current + 1);
        setLoading(false);
      } catch (err) {
        if (!mounted) {
          return;
        }

        setError(err.message || "地図の読み込みに失敗しました");
        setLoading(false);
      }
    };

    loadSvg();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedPrefecture) {
      return undefined;
    }

    const host = mapHostRef.current;
    if (!host) {
      return undefined;
    }

    host.innerHTML = prefectureHtml;

    const svg = host.querySelector("svg");
    if (!svg) {
      return undefined;
    }

    const nodes = Array.from(svg.querySelectorAll("[data-code]"));
    const cleanup = [];

    nodes.forEach((node) => {
      const code = String(node.getAttribute("data-code") || "");
      const row = rowByPrefCode[code];
      const fillColor = row ? colorScale(row.achievedPercent) : NO_DATA_FILL;
      const clickable = Boolean(row && row.cityRows && row.cityRows.length > 0);

      applyFillToNode(node, fillColor);
      applyStrokeToNode(node, BORDER_GRAY, "1.15px");
      d3.select(node).style("cursor", clickable ? "pointer" : "default");

      const handleMouseEnter = (event) => {
        if (row) {
          applyStrokeToNode(node, "#222", "2px");
        }

        if (!mapStageRef.current) {
          return;
        }

        const bounds = mapStageRef.current.getBoundingClientRect();
        setTooltip({
          visible: true,
          title: row ? row.prefectureName : "データなし",
          lines: buildTooltipLines(row),
          x: event.clientX - bounds.left + 12,
          y: event.clientY - bounds.top + 12,
        });
      };

      const handleMouseLeave = () => {
        applyStrokeToNode(node, BORDER_GRAY, "1.15px");
        setTooltip(null);
      };

      const handleClick = () => {
        if (clickable) {
          setSelectedPrefecture(row);
          setTooltip(null);
        }
      };

      node.addEventListener("mouseenter", handleMouseEnter);
      node.addEventListener("mouseleave", handleMouseLeave);
      node.addEventListener("click", handleClick);

      cleanup.push(() => {
        node.removeEventListener("mouseenter", handleMouseEnter);
        node.removeEventListener("mouseleave", handleMouseLeave);
        node.removeEventListener("click", handleClick);
      });
    });

    appendPrefectureLabels(svg, rowByPrefCode, classes);

    return () => {
      cleanup.forEach((fn) => fn());
      clearLabelLayer(svg);
    };
  }, [prefectureHtml, rowByPrefCode, selectedPrefecture, svgReadyKey]);

  const handleBackToNation = () => {
    setSelectedPrefecture(null);
    setTooltip(null);
  };

  const handleMunicipalityHover = (event, row, feature) => {
    if (!mapStageRef.current) {
      return;
    }

    const bounds = mapStageRef.current.getBoundingClientRect();
    const title =
      row?.cityName ||
      feature?.properties?.name ||
      feature?.properties?.N03_004 ||
      "市区町村";

    setTooltip({
      visible: true,
      title,
      lines: buildTooltipLines(row),
      x: event.clientX - bounds.left + 12,
      y: event.clientY - bounds.top + 12,
    });
  };

  const handleMunicipalityLeave = () => {
    setTooltip(null);
  };

  return (
    <div className={classes.root}>
      {!selectedPrefecture ? (
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.titleBlock}>
              <Typography className={classes.title}>都道府県別 MAP</Typography>
              <Typography className={classes.subtitle}>
                達成率を都道府県ごとに塗り分け
              </Typography>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <IconButton
              className={classes.backButton}
              onClick={handleBackToNation}
              size="small"
              aria-label="全国へ戻る"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <div className={classes.titleBlock}>
              <Typography className={classes.title}>
                {selectedPrefecture.prefectureName} 市区町村 MAP
              </Typography>
              <Typography className={classes.subtitle}>
                左矢印クリックで全国地図に戻れます
              </Typography>
            </div>
          </div>
        </div>
      )}

      <Paper className={classes.mapPaper} elevation={0} variant="outlined">
        <div className={classes.mapLayout}>
          <div className={classes.mapColumn}>
            <div className={classes.mapStage} ref={mapStageRef}>
              {!selectedPrefecture ? (
                <div className={classes.mapHost} ref={mapHostRef} />
              ) : (
                <MunicipalityMap
                  selectedPrefecture={selectedPrefecture}
                  onBack={handleBackToNation}
                  onHover={handleMunicipalityHover}
                  onLeave={handleMunicipalityLeave}
                />
              )}

              {loading && !selectedPrefecture && (
                <div className={classes.loading}>地図を読み込み中...</div>
              )}
              {error && !selectedPrefecture && (
                <div className={classes.error}>{error}</div>
              )}
              {tooltip && tooltip.visible && (
                <div
                  className={classes.tooltip}
                  style={{
                    left: tooltip.x,
                    top: tooltip.y,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltip.title}</div>
                  {tooltip.lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={classes.legendColumn}>
            <div className={classes.legend}>
              <Typography className={classes.legendLabel}>達成率</Typography>
              {legendItems.map((item) => (
                <div className={classes.legendItem} key={item.label}>
                  <span
                    className={classes.legendSwatch}
                    style={{ backgroundColor: colorScale(item.value) }}
                  />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default ByAreaMap;
